import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Globe2, X, MessageCircle } from "lucide-react";
import { FLORIDA_COUNTIES, REGIONS } from "@/data/services";
import ContourBackground from "@/components/ContourBackground";
import StatesSlider from "@/components/StatesSlider";
import ServiceCheckboxPicker from "@/components/ServiceCheckboxPicker";
import useSeo from "@/hooks/useSeo";
import { withAttribution } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", company: "", phone: "", country: "USA", service: "", message: "" };

export default function Contact() {
  useSeo({
    title: "Contact — Tri-Axis XYZ CAD Solutions",
    description: "Get a quote on CAD drafting & survey production. Florida HQ — serving surveying firms across the USA, Canada and Australia.",
  });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [prefilled, setPrefilled] = useState(null);

  // Read query params to pre-fill from StatesSlider deep-link.
  // Mount-only: searchParams may change on later navigation (e.g. user clears the
  // pre-fill chip) but re-firing this would clobber any edits the user made to the
  // form. Intentionally empty deps; the cleanup-then-refill flow is handled by
  // clearPrefill().
  useEffect(() => {
    const country = searchParams.get("country");
    const state = searchParams.get("state");
    const code = searchParams.get("code");
    if (state && code) {
      setForm((f) => ({
        ...f,
        country: country || f.country,
        message:
          `Hi Tri-Axis team,\n\nI'm looking for CAD drafting support in ${state} (${code}). ` +
          `Please share availability, typical turnaround, and rates for projects in this region.\n\nThanks!`,
      }));
      setPrefilled({ state, code, country });
      // Scroll the form into view
      setTimeout(() => {
        document.querySelector('[data-testid="contact-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, []);

  const clearPrefill = () => {
    setPrefilled(null);
    setForm(EMPTY);
    navigate("/contact", { replace: true });
  };
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email, and message are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, withAttribution({ ...form, source: prefilled ? `state-slider-${prefilled.code}` : "contact-form" }));
      toast.success("Message received. We'll reply within 1 business day.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Submission failed. Email triaxisxyzsolutions@gmail.com directly.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="page-contact">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-paper-dark/40">
        <ContourBackground variant="light" opacity={0.55} seed={17} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Contact · Quote · Brief</div>
            <h1 className="display mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[1.02]">
              Let's draft your next{" "}
              <span className="text-emerald-700">CAD project</span> — together.
            </h1>
            <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
              Tell us about your firm and the deliverables you need — survey CAD,
              architectural drafting, structural detailing, MEP / BIM coordination,
              mechanical or specialty CAD. We typically respond within one business
              day, including weekends in busy seasons.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {[
                "ALTA / NSPS",
                "Boundary & Topo",
                "Subdivision Plats",
                "Construction Staking",
                "Architectural",
                "Structural Detailing",
                "MEP / BIM",
                "Mechanical / Electrical",
                "PDF-to-CAD",
                "3D Rendering",
              ].map((k) => (
                <span
                  key={k}
                  className="inline-block mono text-[10px] tracking-[0.22em] uppercase border border-slate-300 bg-white px-2.5 py-1 text-slate-700"
                >
                  {k}
                </span>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="border border-slate-200 bg-white p-6 lg:p-7 corner-marks">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
                Our promise on every brief
              </div>
              <ul className="mt-4 space-y-3">
                {[
                  { k: "Reply within 24 hours", v: "A senior CAD lead reads every inbound and replies within one business day." },
                  { k: "Fixed-price quote", v: "Send a sample or scope — we come back with a milestone schedule and a fixed price." },
                  { k: "Sample on request", v: "We can produce a paid 1-drawing pilot before any retainer or hourly agreement." },
                  { k: "NDA & secure transfer", v: "Mutual NDA, encrypted file transfer, 60-day retention by default." },
                ].map((item) => (
                  <li key={item.k} className="flex items-start gap-3">
                    <span className="mt-1 inline-block h-2 w-2 bg-orange-survey shrink-0" />
                    <div>
                      <div className="display text-sm font-extrabold tracking-tight text-slate-900">
                        {item.k}
                      </div>
                      <div className="text-sm text-slate-600 leading-relaxed">{item.v}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-5 pt-4 border-t border-slate-100 grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="display text-2xl font-extrabold tracking-tighter text-topo">23</div>
                  <div className="mono text-[9px] tracking-[0.22em] uppercase text-slate-500 mt-0.5">Service lines</div>
                </div>
                <div>
                  <div className="display text-2xl font-extrabold tracking-tighter text-topo">&lt;24h</div>
                  <div className="mono text-[9px] tracking-[0.22em] uppercase text-slate-500 mt-0.5">First reply</div>
                </div>
                <div>
                  <div className="display text-2xl font-extrabold tracking-tighter text-topo">120+</div>
                  <div className="mono text-[9px] tracking-[0.22em] uppercase text-slate-500 mt-0.5">Firms served</div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Form + Sidebar */}
      <section id="contact-brief" className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7 border border-slate-200 p-6 lg:p-8 bg-white">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Project brief</div>
            <h2 className="display mt-3 text-2xl sm:text-3xl font-extrabold tracking-tighter text-slate-900">
              Send us your details
            </h2>

            {prefilled && (
              <div
                data-testid="prefill-banner"
                className="mt-5 flex items-start justify-between gap-3 border border-emerald-700/30 bg-emerald-50 p-4 rounded-sm"
              >
                <div>
                  <div className="mono text-[10px] tracking-[0.22em] uppercase text-emerald-700 flex items-center gap-1.5">
                    <MapPin className="h-3 w-3" /> Routing to {prefilled.code} specialist
                  </div>
                  <p className="mt-1 text-sm text-slate-700">
                    We've pre-filled your request for <strong>{prefilled.state}</strong>
                    {prefilled.country ? <> · {prefilled.country}</> : null}. Add your details below and send.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={clearPrefill}
                  data-testid="prefill-clear"
                  aria-label="Clear pre-fill"
                  className="inline-flex items-center justify-center h-7 w-7 rounded-sm border border-emerald-700/30 hover:bg-emerald-700 hover:text-white text-emerald-700 transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full name *" id="contact-name" value={form.name} onChange={update("name")} testid="contact-input-name" />
              <Field label="Work email *" id="contact-email" type="email" value={form.email} onChange={update("email")} testid="contact-input-email" />
              <Field label="Company" id="contact-company" value={form.company} onChange={update("company")} testid="contact-input-company" />
              <Field label="Phone" id="contact-phone" value={form.phone} onChange={update("phone")} testid="contact-input-phone" />

              <Select
                label="Country"
                id="contact-country"
                value={form.country}
                onChange={update("country")}
                testid="contact-input-country"
                options={REGIONS.map((r) => ({ value: r.name, label: `${r.flag} ${r.name}` }))}
              />
              <ServiceCheckboxPicker
                value={form.service}
                onChange={(v) => setForm((f) => ({ ...f, service: v }))}
              />
            </div>

            <div className="mt-4">
              <label htmlFor="contact-message" className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">Project message *</label>
              <textarea
                id="contact-message"
                data-testid="contact-input-message"
                rows={6}
                value={form.message}
                onChange={update("message")}
                placeholder="Scope, deadlines, CAD standards, file formats..."
                className="mt-2 w-full border border-slate-300 focus:border-topo outline-none p-3 text-sm rounded-sm bg-white"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="mt-6 inline-flex items-center gap-2 rounded-sm bg-orange-survey hover:bg-orange-survey-dark disabled:opacity-60 text-white text-sm font-semibold px-5 py-3"
            >
              <Send className="h-4 w-4" /> {loading ? "Sending..." : "Send Message"}
            </button>
          </form>

          <aside className="lg:col-span-5 space-y-px">
            <div className="bg-[#0B1220] text-white p-6 lg:p-8 border border-[#1F2A44]">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-emerald-400">Direct contact</div>
              <div className="display mt-2 text-2xl font-extrabold tracking-tight">Tri-Axis XYZ CAD Solutions, LLC</div>
              <ul className="mt-5 space-y-3 text-sm">
                <li className="flex items-start gap-3"><Mail className="h-4 w-4 mt-0.5 text-emerald-400" /><a href="mailto:triaxisxyzsolutions@gmail.com" data-testid="contact-email-link" className="hover:underline font-semibold">triaxisxyzsolutions@gmail.com</a></li>
                <li className="flex items-start gap-3"><Mail className="h-4 w-4 mt-0.5 text-slate-500" /><a href="mailto:contact@triaxisxyz.com" data-testid="contact-email-alt-link" className="hover:underline text-slate-400">contact@triaxisxyz.com</a></li>
                <li className="flex items-start gap-3">
                  <Phone className="h-4 w-4 mt-0.5 text-emerald-400" />
                  <div className="flex flex-col">
                    <a href="tel:+918083691588" data-testid="contact-phone-link" className="hover:underline font-semibold">+91 80836 91588</a>
                    <span className="mono text-[10px] tracking-[0.22em] uppercase text-emerald-400/90 mt-0.5 inline-flex items-center gap-1">
                      <MessageCircle className="h-3 w-3" /> WhatsApp · Call · Text
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-3"><MapPin className="h-4 w-4 mt-0.5 text-emerald-400" /><span>200 S Orange Ave, Orlando, FL 32801, USA</span></li>
                <li className="flex items-start gap-3"><Globe2 className="h-4 w-4 mt-0.5 text-emerald-400" /><span>triaxisxyz.com</span></li>
              </ul>
            </div>
            <div className="border border-slate-200 bg-white p-6 lg:p-8">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Office hours</div>
              <ul className="mt-3 mono text-sm text-slate-700 space-y-1">
                <li>MON–FRI · 08:00–20:00 EST</li>
                <li>SAT · 10:00–14:00 EST</li>
                <li>SUN · On-call (busy season)</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* Florida Map */}
      <FloridaMap />

      {/* North American state slider */}
      <StatesSlider
        heading="Coverage across the U.S., Canada & Australia."
        subheading="From Florida to Ontario to New South Wales — we draft to local standards. Use the slider below to browse every actively-served state and province."
      />

      {/* Counties index */}
      <CountiesIndex />
    </div>
  );
}

function Field({ label, id, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label htmlFor={id} className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">{label}</label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="mt-2 w-full border border-slate-300 focus:border-topo outline-none p-3 text-sm rounded-sm bg-white"
      />
    </div>
  );
}

function Select({ label, id, value, onChange, testid, options }) {
  return (
    <div>
      <label htmlFor={id} className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">{label}</label>
      <select
        id={id}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="mt-2 w-full border border-slate-300 focus:border-topo outline-none p-3 text-sm rounded-sm bg-white"
      >
        {options.map((o) => <option key={o.value || o.label} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function FloridaMap() {
  return (
    <section id="contact-map" className="border-b border-slate-200">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Florida demo coverage</div>
          <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900 leading-tight">
            Florida HQ.<br /><span className="text-emerald-700">Eight counties</span> covered.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-md">
            Our Orlando production hub serves surveying firms across Florida's most active counties — and remote-delivers nationwide.
          </p>
          <div className="mt-6 mono text-xs text-slate-500 grid grid-cols-2 gap-y-1">
            <div>LAT · 28.5383° N</div><div>LON · 81.3792° W</div>
            <div>FIPS · 12095</div><div>STATE · FL</div>
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="corner-marks text-slate-900 border border-slate-200 overflow-hidden bg-paper-dark/40">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/d8d95525-a408-489d-8aaa-625abe3f4e8c/images/140157ed486bfb6f7933aa899d7e742e71baf67a182b89a25b677f19746b7bb6.png"
              alt="Florida technical map"
              className="w-full h-[420px] object-cover"
            />
          </div>
          <div className="mt-3 border border-slate-200 overflow-hidden">
            <iframe
              data-testid="contact-google-map"
              title="Tri-Axis XYZ Orlando HQ"
              src="https://maps.google.com/maps?q=Orlando+Florida&t=&z=10&ie=UTF8&iwloc=&output=embed"
              className="w-full h-[280px] border-0"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function CountiesIndex() {
  return (
    <section id="contact-counties">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-10">
          <div className="lg:col-span-7">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Florida county index · Demo</div>
            <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900">
              County coverage & <span className="text-emerald-700">ZIP pincodes</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 text-slate-600">
            Sample pincodes for our most active service counties. Need a county not listed? Drop us a line.
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200" data-testid="counties-index">
          {FLORIDA_COUNTIES.map((c, i) => (
            <div key={c.name} className="bg-white p-5">
              <div className="flex items-baseline justify-between">
                <div className="display text-lg font-extrabold tracking-tight text-slate-900">{c.name}</div>
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-slate-400">{String(i + 1).padStart(2, "0")}</div>
              </div>
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-emerald-700 mt-1">{c.seat}</div>
              <ul className="mt-4 mono text-xs text-slate-700 space-y-0.5">
                {c.pincodes.map((p) => (
                  <li key={p} className="flex items-center justify-between border-b border-dashed border-slate-200 py-0.5">
                    <span>{p}</span>
                    <span className="text-slate-400">FL · {c.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
